import { useCanViewEmails } from '../../helpers/useCanViewEmails';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import KeyboardArrowDownIcon from '@mui/icons-material/South';
import { useSnackbar } from 'material-ui-snackbar-provider';
import KeyboardArrowUpIcon from '@mui/icons-material/North';
import ArrowBack from '@mui/icons-material/ArrowBack';
import AddIcon from '@mui/icons-material/Add';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { useHistory, useLocation } from 'react-router-dom';
import { useDebounce } from 'usehooks-ts';
import { isNumber } from 'lodash';
import cx from 'classnames';
import TrashIcon from '@mui/icons-material/FolderDelete';
import FolderOffIcon from '@mui/icons-material/FolderOff';
import { Tooltip } from '@mui/material';
import Checkbox from '@mui/material/Checkbox';

import { updateComposerPixelPosition, updateComposerPlayControls, updateProgressPosition } from '../../store/composers/composersSlice';
import { saveCurrentPlayingTimelineClip, updateNotes, updateQualifiers } from '../../store/videos/videoSlice';
import {
  updateFeedTransferMode,
  updateFeedsToMove,
  updateShowMoveFeedModal,
  updateSortOption,
  updateSortType,
} from '../../store/videoFeedsList/videoFeedsListSlice';
import VideofeedShareModal from '../SharingModals/MyFolderPage/VideofeedShareModal';
import ShareFolderModal from '../SharingModals/MyFolderPage/ShareFolderModal';
import { switchSidebar, updateShowWarningModal } from '../../store/sharedStore/sharedStoreSlice';
import { useAppDispatch, useAppSelector } from '../../store/hooks';
import { PaginationFooter } from '../modules/PaginationFooter';
import { FOLDER_TYPE, VIDEOFEED } from '../../enums/TYPES';
import { FOLDER_CONTENT_FILTER, NOTIFICATION, VIDEOFEED_TYPE } from '../../enums/ENUMS';
import { SortOptions, SortType } from '../../enums/SORT';
import { getFolderContents } from '../../api/videofeeds';
import LinearProgress from '../modules/LinearProgress';
import MoveVideoFeedModal from './MoveVideoFeedModal';
import { beginMoveUndo, rememberFolderLocations } from '../../helpers/moveUndo';
import styles from './VideofeedsList.module.scss';
import deleteModalStyles from './DeleteVideofeedModal/DeleteVideofeedModal.module.scss';
import SearchInput from '../modules/SearchInput';
import {
  deleteFolderById,
  deleteVideofeed,
  getFolderDeleteImpact,
  getShallowFolder,
  getUserOrganization,
  patchMoveFolders,
  patchMoveVideofeeds,
} from '../../api';
import {
  getMyFoldersSortOptionAndType,
  getMyVideofeedsPagePaginationLimit,
  getSortStateForMyFolders,
  setMyFoldersSortOptionAndType,
  setMyVideofeedsPagePaginationLimit,
  useDimensions,
} from '../../helpers';
import Videofeed from './Videofeed';
import { PATHS } from '../../enums';
import DeletedVideofeedsModal from './DeletedVideofeedsModal';
import DeletedFoldersModal from './DeletedFoldersModal';
import { checkContentAvailability } from '../../api/accessesApi';
import { ReactComponent as UsersIcon } from '../../assets/icons/CollaboratorsUsersIcon.svg';
import ComposerFolder from '../modules/Folders/ComposerFolder';
import Modal from '../modules/Modal';
import { ContainedButton, OutlinedButton } from '../modules/Buttons';
import { saveMyOrganization } from '../../store/user/userSlice';
import { updateTriggerFolderFetch } from '../../store/folders/foldersSlice';
import MoveButton from '../modules/MoveButton';
import MoveFoldersModal, { buildMovedVideofeedUrl } from '../MyFolders/MoveFoldersModal';
import { getPinTargetType } from '../../helpers/pinnedContent';
import DateSortHeader from '../modules/DateSortHeader';
import { FilterIcon } from '../../assets/icons/filterIcon';
import VideofeedFiltersModal, {
  FilterOption,
  VIDEOFEED_STATUS_FILTER,
} from './VideofeedFiltersModal';
import { RolesLowerCase } from '../../enums/ROLES';
import CreateFolderFromDrop from '../modules/ContentDragDrop/CreateFolderFromDrop';
import { useContentDragDrop } from '../modules/ContentDragDrop/useContentDragDrop';
import PinnedContentSection from '../modules/PinnedContentSection';

type FOLDER_CONTENT_ITEM =
  | (FOLDER_TYPE & { item_type: 'folder' })
  | (VIDEOFEED & { item_type?: 'videofeed' });

const getContentFilter = (value?: string | null): FOLDER_CONTENT_FILTER => {
  switch (value) {
    case FOLDER_CONTENT_FILTER.TIMELINE:
    case FOLDER_CONTENT_FILTER.COMPOSER:
    case FOLDER_CONTENT_FILTER.SUBFOLDER:
      return value;
    default:
      return FOLDER_CONTENT_FILTER.ALL;
  }
};

const isCollectionDeleteTarget = (feed: VIDEOFEED) =>
  Boolean(feed?.is_collection_root) ||
  (!feed?.parent_videofeed_id && Number(feed?.movie_collection_id || 0) > 0) ||
  Boolean(feed?.has_reference_timelines) ||
  Boolean(feed?.reference_timelines?.length);

const getItemSelectionKey = (item: FOLDER_CONTENT_ITEM) =>
  item.item_type === 'folder'
    ? `folder:${item.id}`
    : `${isCollectionDeleteTarget(item) ? 'collection' : 'videofeed'}:${item.id}`;

const getReferenceSelectionKey = (feed: VIDEOFEED) => `reference:${feed.id}`;

const isSelectableReferenceTimeline = (feed: VIDEOFEED) =>
  feed.type === VIDEOFEED_TYPE.TIMELINE &&
  (Number(feed.movie_collection_id || 0) > 0 ||
    (feed.parent_videofeed_id !== null &&
      feed.parent_videofeed_id !== undefined));

const getSelectableReferenceTimelines = (feed: VIDEOFEED) =>
  (feed.reference_timelines || []).filter(isSelectableReferenceTimeline);

const VideofeedsList = () => {
  const canViewEmails = useCanViewEmails();
  const [folderContents, setFolderContents] = useState<FOLDER_CONTENT_ITEM[]>(
    [],
  );
  const notification = useSnackbar();
  const dispatch = useAppDispatch();
  const [loading, setLoading] = useState(false);
  const requestIdRef = useRef(0);

  const { refetchFeedList, sortOption, sort } = useAppSelector(
    (state) => state.videoFeedsListStore,
  );
  const { composerPlayControl } = useAppSelector(
    (state) => state.composersStore,
  );
  const { triggerFolderFetch } = useAppSelector((state) => state.folderStore);
  const { currentUser } = useAppSelector((state) => state.user);

  const { widthD } = useDimensions();
  const location = useLocation();
  const history = useHistory();

  const searchParams = new URLSearchParams(location.search);
  const folderId = searchParams.get('folderId');
  const folderTrail = searchParams.get('folderTrail');
  const [contentFilter, setContentFilter] = useState<FOLDER_CONTENT_FILTER>(
    () => getContentFilter(searchParams.get('contentFilter')),
  );
  const [status, setStatus] = useState<VIDEOFEED_STATUS_FILTER>(
    VIDEOFEED_STATUS_FILTER.ALL,
  );
  const [selectedOwners, setSelectedOwners] = useState<FilterOption[]>([
    { label: 'All', value: 'ALL' },
  ]);
  const [filtersModalOpen, setFiltersModalOpen] = useState(false);
  const breadcrumbAncestorIds = useMemo(
    () => (folderTrail ? folderTrail.split(',').filter(Boolean) : []),
    [folderTrail],
  );

  useEffect(() => {
    setContentFilter(getContentFilter(searchParams.get('contentFilter')));
  }, [location.search]);

  const runCheck = async (type: 'folder' | 'videofeed', id: string) => {
    try {
      const res = await checkContentAvailability(type, id);
      if (res.data?.deleted) {
        dispatch(
          updateShowWarningModal({
            show: true,
            warningText:
              'The content you are trying to access has been deleted',
            redirect: true,
          }),
        );
      } else if (res.data?.noAccess) {
        dispatch(
          updateShowWarningModal({
            show: true,
            warningText:
              'You do not have permission to access the content you are trying to view.',
            redirect: true,
          }),
        );
      } else if (res?.data?.differentOrg) {
        dispatch(
          updateShowWarningModal({
            show: true,
            warningText: `Your current organisation is different from the content's organisation.${
              res?.data?.organizationName
                ? ` The content is from ${res.data.organizationName}.`
                : ''
            }`,
            redirect: true,
          }),
        );
      }
    } catch (err) {
      console.error(err);
    }
  };

  const [folder, setFolder] = useState<FOLDER_TYPE>();
  const [breadcrumbFolders, setBreadcrumbFolders] = useState<FOLDER_TYPE[]>([]);
  useEffect(() => {
    if (folderId) {
      fetchFolder(folderId);
    }
  }, [folderId]);

  useEffect(() => {
    let active = true;

    const loadBreadcrumbFolders = async () => {
      if (!breadcrumbAncestorIds.length) {
        setBreadcrumbFolders([]);
        return;
      }

      try {
        const responses = await Promise.all(
          breadcrumbAncestorIds.map((id) => getShallowFolder(id)),
        );

        if (active) {
          setBreadcrumbFolders(responses.map((response) => response.data));
        }
      } catch (err) {
        if (active) {
          setBreadcrumbFolders([]);
        }
      }
    };

    loadBreadcrumbFolders();

    return () => {
      active = false;
    };
  }, [breadcrumbAncestorIds]);

  const fetchFolder = async (id: string) => {
    try {
      runCheck('folder', id);
      const res = await getShallowFolder(id);
      setFolder(res.data);
    } catch (err: any) {
      console.error(err);
    }
  };

  const [search, setSearch] = useState('');
  const debouncedValue = useDebounce<string>(search, 500);

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [total, setTotal] = useState(0);
  const [selectMode, setSelectMode] = useState(false);
  const [selectedItemKeys, setSelectedItemKeys] = useState<string[]>([]);
  const [showBulkDeleteModal, setShowBulkDeleteModal] = useState(false);
  const [bulkDeleteLoading, setBulkDeleteLoading] = useState(false);
  const [bulkDeleteImpactLoading, setBulkDeleteImpactLoading] = useState(false);
  const [bulkDeleteImpactedComposers, setBulkDeleteImpactedComposers] = useState<string[]>([]);
  const [showMoveFoldersModal, setShowMoveFoldersModal] = useState(false);
  const [foldersToMove, setFoldersToMove] = useState<
    (FOLDER_TYPE & { item_type: 'folder' })[]
  >([]);
  const [feedsToMoveWithFolders, setFeedsToMoveWithFolders] = useState<
    VIDEOFEED[]
  >([]);
  const [groupFiles, setGroupFiles] = useState<[VIDEOFEED, VIDEOFEED] | null>(null);
  const draggedMoveItemRef = useRef<FOLDER_CONTENT_ITEM | null>(null);
  const [draggedRowKey, setDraggedRowKey] = useState<string | null>(null);
  const [navigationDropTargetKey, setNavigationDropTargetKey] = useState<string | null>(null);

  useEffect(() => {
    dispatch(updateNotes([]));
    dispatch(saveCurrentPlayingTimelineClip(null));
    dispatch(updateQualifiers([]));
    const prefferedLimit = getMyVideofeedsPagePaginationLimit();
    if (prefferedLimit && isNumber(+prefferedLimit)) {
      setRowsPerPage(+prefferedLimit);
      onPaginate(null, 0);
    }
    dispatch(switchSidebar(false));
    dispatch(updateProgressPosition(0));
    dispatch(
      updateComposerPlayControls({
        ...composerPlayControl.composerControls,
        playedSeconds: 0,
      }),
    );
    dispatch(updateComposerPixelPosition(0));
  }, [currentUser?.id]);

  const debounceRef = useRef<number | null>(null);
  const fetchFolderContentsDebounced = async (arg?: {
    sortFromArg?: SortType;
    sortOpinionFromArg?: SortOptions;
    signal?: AbortSignal;
  }) => {
    requestIdRef.current += 1;
    const currentRequestId = requestIdRef.current;
    setLoading(true);
    const prefferedLimit = getMyVideofeedsPagePaginationLimit();

    try {
      const res = await getFolderContents({
        folderId: folderId as string,
        page,
        limit:
          prefferedLimit && isNumber(+prefferedLimit)
            ? +prefferedLimit
            : rowsPerPage,
        search,
        sort: arg?.sortFromArg ? arg.sortFromArg : sort,
        sortOption: arg?.sortOpinionFromArg
          ? arg.sortOpinionFromArg
          : sortOption,
        view: 'mine',
        contentFilter,
        status,
        ownerIds:
          selectedOwners[0]?.value === 'ALL'
            ? undefined
            : selectedOwners.map((owner) => owner.value),
        signal: arg?.signal,
      });
      if (currentRequestId !== requestIdRef.current) {
        return;
      }
      setFolderContents(res.documents);
      setTotal(res.total);
    } catch (err: any) {
      if (err.name === 'AbortError') {
        return;
      }

      if (err?.response?.data?.message) {
        history.push(PATHS.MY_FOLDERS.path);
        notification.showMessage(err?.response?.data?.message);
      }
    } finally {
      if (currentRequestId === requestIdRef.current) {
        setLoading(false);
      }
    }
  };

  const fetchVideofeeds = (
    arg?: Parameters<typeof fetchFolderContentsDebounced>[0],
    delay = 300,
  ) => {
    if (debounceRef.current) window.clearTimeout(debounceRef.current);
    debounceRef.current = window.setTimeout(() => {
      fetchFolderContentsDebounced(arg);
    }, delay);
  };

  const refreshFolderContents = () => {
    setFetchTrigger({ at: Date.now() });
  };

  const [fetchTrigger, setFetchTrigger] = useState({});
  useEffect(() => {
    const abortController = new AbortController();
    const res = getMyFoldersSortOptionAndType();
    if (res) {
      const sortAndOption = res.split('/');
      if (sortAndOption[0] && sortAndOption[1]) {
        dispatch(updateSortType(sortAndOption[0]));
        dispatch(updateSortOption(sortAndOption[1]));
        fetchVideofeeds({
          sortFromArg: sortAndOption[0] as SortType,
          sortOpinionFromArg: sortAndOption[1] as SortOptions,
          signal: abortController.signal,
        });
      }
    } else if (folderId) {
      fetchVideofeeds({ signal: abortController.signal });
    }

    return () => {
      abortController.abort();
    };
  }, [
    debouncedValue,
    page,
    rowsPerPage,
    sort,
    refetchFeedList,
    sortOption,
    fetchTrigger,
    folderId,
    triggerFolderFetch,
    contentFilter,
    status,
    selectedOwners,
  ]);

  const onSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearch(e.target.value);
    onPaginate(null, 0);
  };

  const onRowsChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    setRowsPerPage(parseInt(e.target.value, 10));
    setMyVideofeedsPagePaginationLimit(parseInt(e.target.value, 10));
    onPaginate(null, 0);
  };

  const onPaginate = (
    e: React.MouseEvent<HTMLButtonElement, MouseEvent> | null,
    newPage: number,
  ) => {
    setPage(newPage);
    if (listRef.current) {
      listRef.current.scrollTop = 0;
    }
  };

  const onContentFilterSelect = (nextFilter: FOLDER_CONTENT_FILTER) => {
    const nextSearch = new URLSearchParams(location.search);

    if (nextFilter === FOLDER_CONTENT_FILTER.ALL) {
      nextSearch.delete('contentFilter');
    } else {
      nextSearch.set('contentFilter', nextFilter);
    }

    history.replace(
      `${location.pathname}${nextSearch.toString() ? `?${nextSearch.toString()}` : ''}`,
    );
    setContentFilter(nextFilter);
    onPaginate(null, 0);
  };

  const filterByStatus = (nextStatus: VIDEOFEED_STATUS_FILTER) => {
    setStatus(nextStatus);
    onPaginate(null, 0);
  };

  const filterByOwner = (owners: FilterOption[]) => {
    setSelectedOwners(owners);
    onPaginate(null, 0);
  };

  const switchFiltersModal = () => {
    if (!loading) {
      setFiltersModalOpen((open) => !open);
    }
  };

  const sortHandler = (
    singleValue: SortOptions,
    option?: { label: string; value: string; order: SortType },
  ) => {
    const selectedSortOption = option
      ? (option.value as SortOptions)
      : singleValue;
    if (selectedSortOption === SortOptions.OWNER && !showOwnerColumn) {
      return;
    }

    if (option) {
      dispatch(updateSortType(option.order));
      setMyFoldersSortOptionAndType(option.order, option.value as SortOptions);
    } else if (sortOption === singleValue) {
      const sortT = sort === SortType.ASC ? SortType.DESC : SortType.ASC;
      dispatch(updateSortType(sortT));
      setMyFoldersSortOptionAndType(sortT, singleValue);
    } else {
      const sortState = JSON.parse(getSortStateForMyFolders()!);
      let sortType = SortType.ASC;
      if (sortState && sortState[singleValue]) {
        sortType = sortState[singleValue];
      }
      dispatch(updateSortType(sortType));
      setMyFoldersSortOptionAndType(sortType, singleValue);
    }
    dispatch(updateSortOption(selectedSortOption));
    onPaginate(null, 0);
  };

  const sortFromFilters = (
    selectedSortOption: SortOptions,
    selectedSortOrder: SortType,
  ) => {
    sortHandler(selectedSortOption, {
      label: '',
      value: selectedSortOption,
      order: selectedSortOrder,
    });
  };

  const redirectToFoldes = () => {
    history.push(PATHS.MY_FOLDERS.path);
  };

  const onBreadcrumbClick = (targetFolderId?: string, ancestorIndex?: number) => {
    if (!targetFolderId) {
      redirectToFoldes();
      return;
    }

    setPage(0);

    const nextTrail =
      typeof ancestorIndex === 'number' && ancestorIndex > 0
        ? breadcrumbAncestorIds.slice(0, ancestorIndex).join(',')
        : '';

    history.push(
      `${PATHS.VIDEOFEEDS_LIST.path}?folderId=${targetFolderId}&parentPage=${PATHS.MY_FOLDERS.label}${
        nextTrail ? `&folderTrail=${encodeURIComponent(nextTrail)}` : ''
      }${
        contentFilter !== FOLDER_CONTENT_FILTER.ALL
          ? `&contentFilter=${contentFilter}`
          : ''
      }`,
    );
  };

  const onHierarchyBackClick = () => {
    const parentFolderId =
      breadcrumbAncestorIds[breadcrumbAncestorIds.length - 1];
    if (!parentFolderId) {
      onBreadcrumbClick();
      return;
    }

    onBreadcrumbClick(
      String(parentFolderId),
      breadcrumbAncestorIds.length - 1,
    );
  };

  const listRef = useRef<HTMLDivElement>(null);
  const autoScrollFrameRef = useRef<number | null>(null);
  const autoScrollDirectionRef = useRef<'up' | 'down' | null>(null);
  const [canScrollUp, setCanScrollUp] = useState(false);
  const [canScrollDown, setCanScrollDown] = useState(false);

  const updateListScrollControls = () => {
    const list = listRef.current;
    if (!list) return;
    setCanScrollUp(list.scrollTop > 1);
    setCanScrollDown(list.scrollTop + list.clientHeight < list.scrollHeight - 1);
  };

  const stopListAutoScroll = () => {
    autoScrollDirectionRef.current = null;
    if (autoScrollFrameRef.current !== null) {
      cancelAnimationFrame(autoScrollFrameRef.current);
      autoScrollFrameRef.current = null;
    }
  };

  const startListAutoScroll = (direction: 'up' | 'down') => {
    if (autoScrollDirectionRef.current === direction) return;
    stopListAutoScroll();
    autoScrollDirectionRef.current = direction;

    const scroll = () => {
      const list = listRef.current;
      if (!list || autoScrollDirectionRef.current !== direction) return;
      list.scrollTop += direction === 'up' ? -3 : 3;
      if (
        (direction === 'up' && list.scrollTop <= 0) ||
        (direction === 'down' && list.scrollTop + list.clientHeight >= list.scrollHeight - 1)
      ) {
        stopListAutoScroll();
        return;
      }
      autoScrollFrameRef.current = requestAnimationFrame(scroll);
    };

    autoScrollFrameRef.current = requestAnimationFrame(scroll);
  };

  const onAutoScrollDragOver = (
    event: React.DragEvent<HTMLDivElement>,
    direction: 'up' | 'down',
  ) => {
    event.preventDefault();
    event.stopPropagation();
    event.dataTransfer.dropEffect = 'move';
    startListAutoScroll(direction);
  };

  const onAutoScrollDragLeave = (event: React.DragEvent<HTMLDivElement>) => {
    if (!event.currentTarget.contains(event.relatedTarget as Node)) {
      stopListAutoScroll();
    }
  };

  useEffect(() => {
    if (draggedRowKey) {
      requestAnimationFrame(updateListScrollControls);
    } else {
      stopListAutoScroll();
    }

    return stopListAutoScroll;
  }, [draggedRowKey]);

  const [showDeletedVideofeedsModal, setShowDeletedVidefeedsModal] =
    useState(false);
  const [showDeletedFoldersModal, setShowDeletedFoldersModal] =
    useState(false);
  const onTrashIconClick = () => {
    setShowDeletedVidefeedsModal(!showDeletedVideofeedsModal);
  };
  const onDeletedFoldersClick = () => {
    setShowDeletedFoldersModal(!showDeletedFoldersModal);
  };

  const openMoveFoldersModal = (
    targetFolders: (FOLDER_TYPE & { item_type: 'folder' })[],
    targetFeeds: VIDEOFEED[] = [],
  ) => {
    if (!targetFolders.length && !targetFeeds.length) return;
    setFoldersToMove(targetFolders);
    setFeedsToMoveWithFolders(targetFeeds);
    setShowMoveFoldersModal(true);
  };

  const closeMoveFoldersModal = () => {
    setShowMoveFoldersModal(false);
    setFoldersToMove([]);
    setFeedsToMoveWithFolders([]);
  };

  const clearSelection = () => {
    setSelectedItemKeys([]);
    setSelectMode(false);
    setShowBulkDeleteModal(false);
    closeMoveFoldersModal();
  };

  const toggleSelectMode = () => {
    if (selectMode) {
      clearSelection();
      return;
    }

    setSelectMode(true);
  };

  const toggleItemSelection = (item: FOLDER_CONTENT_ITEM) => {
    const itemKey = getItemSelectionKey(item);
    const referenceKeys =
      item.item_type === 'folder'
        ? []
        : getSelectableReferenceTimelines(item).map(getReferenceSelectionKey);

    setSelectedItemKeys((prev) => {
      if (prev.includes(itemKey)) {
        return prev.filter(
          (key) => key !== itemKey && !referenceKeys.includes(key),
        );
      }

      return [...new Set([...prev, itemKey, ...referenceKeys])];
    });
  };

  const toggleReferenceSelection = (timeline: VIDEOFEED) => {
    const referenceKey = getReferenceSelectionKey(timeline);

    setSelectedItemKeys((prev) =>
      prev.includes(referenceKey)
        ? prev.filter((key) => key !== referenceKey)
        : [...prev, referenceKey],
    );
  };

  const selectedItemKeySet = useMemo(
    () => new Set(selectedItemKeys),
    [selectedItemKeys],
  );

  const selectedItems = useMemo(
    () =>
      folderContents.filter((item) =>
        selectedItemKeySet.has(getItemSelectionKey(item)),
      ),
    [folderContents, selectedItemKeySet],
  );

  const selectedFolders = useMemo(
    () =>
      selectedItems.filter(
        (item) => item.item_type === 'folder',
      ) as (FOLDER_TYPE & { item_type: 'folder' })[],
    [selectedItems],
  );

  const selectedTopLevelFeeds = useMemo(
    () =>
      selectedItems.filter((item) => {
        if (item.item_type === 'folder') {
          return false;
        }

        if (!isCollectionDeleteTarget(item)) {
          return true;
        }

        const references = getSelectableReferenceTimelines(item);
        const selectedReferences = references.filter((timeline) =>
          selectedItemKeySet.has(getReferenceSelectionKey(timeline)),
        );

        return (
          selectedReferences.length === 0 ||
          selectedReferences.length === references.length
        );
      }) as VIDEOFEED[],
    [selectedItems, selectedItemKeySet],
  );

  const selectedReferenceFeeds = useMemo(() => {
    const feeds: VIDEOFEED[] = [];

    folderContents.forEach((item) => {
      if (item.item_type === 'folder') {
        return;
      }

      const collectionKey = getItemSelectionKey(item);
      const references = getSelectableReferenceTimelines(item);
      const selectedReferences = references.filter((timeline) =>
        selectedItemKeySet.has(getReferenceSelectionKey(timeline)),
      );

      if (
        selectedItemKeySet.has(collectionKey) &&
        selectedReferences.length === references.length
      ) {
        return;
      }

      selectedReferences.forEach((timeline) => {
        feeds.push({ ...timeline, move_as_reference: true } as VIDEOFEED);
      });
    });

    return feeds;
  }, [folderContents, selectedItemKeySet]);

  const selectedFeeds = useMemo(
    () => [...selectedTopLevelFeeds, ...selectedReferenceFeeds],
    [selectedTopLevelFeeds, selectedReferenceFeeds],
  );

  const selectedTopLevelFeedsForCopy = useMemo(
    () =>
      selectedTopLevelFeeds.filter((feed) => {
        if (!isCollectionDeleteTarget(feed)) {
          return true;
        }

        const references = getSelectableReferenceTimelines(feed);
        const selectedReferences = references.filter((timeline) =>
          selectedItemKeySet.has(getReferenceSelectionKey(timeline)),
        );

        return (
          selectedReferences.length === 0 ||
          selectedReferences.length === references.length
        );
      }),
    [selectedTopLevelFeeds, selectedItemKeySet],
  );

  const selectedReferenceFeedsForCopy = useMemo(() => {
    const feeds: VIDEOFEED[] = [];

    folderContents.forEach((item) => {
      if (item.item_type === 'folder') {
        return;
      }

      const collectionKey = getItemSelectionKey(item);
      const references = getSelectableReferenceTimelines(item);
      const selectedReferences = references.filter((timeline) =>
        selectedItemKeySet.has(getReferenceSelectionKey(timeline)),
      );

      if (
        selectedItemKeySet.has(collectionKey) &&
        selectedReferences.length === references.length
      ) {
        return;
      }

      selectedReferences.forEach((timeline) => {
        feeds.push({ ...timeline, move_as_reference: true } as VIDEOFEED);
      });
    });

    return feeds;
  }, [folderContents, selectedItemKeySet]);

  const selectedFeedsForCopy = useMemo(
    () => [...selectedTopLevelFeedsForCopy, ...selectedReferenceFeedsForCopy],
    [selectedTopLevelFeedsForCopy, selectedReferenceFeedsForCopy],
  );

  const isMovableFolder = (folder: FOLDER_TYPE) =>
    (!folder.organization?.id ||
      String(folder.organization.id) === String(currentUser?.organization?.id)) &&
    !folder.is_collaborative;

  const canMoveFoldersByRole = [
    RolesLowerCase.ORGANIZATION_ADMIN,
    RolesLowerCase.ANALYST,
    RolesLowerCase.SUPER_ADMIN,
  ].includes(currentUser?.role?.title as RolesLowerCase);
  const canMoveVideofeedsByRole = [
    RolesLowerCase.ORGANIZATION_ADMIN,
    RolesLowerCase.ANALYST,
    RolesLowerCase.VIEWER,
    RolesLowerCase.SUPER_ADMIN,
  ].includes(currentUser?.role?.title as RolesLowerCase);
  const canMoveFolderItem = (folder: FOLDER_TYPE) =>
    canMoveFoldersByRole && isMovableFolder(folder);
  const canDragMoveItem = (item: FOLDER_CONTENT_ITEM) =>
    item.item_type === 'folder'
      ? canMoveFolderItem(item)
      : canMoveVideofeedsByRole;
  const canMoveItemToRoot = (item: FOLDER_CONTENT_ITEM) =>
    item.item_type === 'folder' &&
    canMoveFolderItem(item) &&
    String(item.owner?.id) === String(currentUser?.id);
  const canDropOnNavigationTarget = (
    item: FOLDER_CONTENT_ITEM,
    destination: FOLDER_TYPE | null,
  ) =>
    destination
      ? (item.item_type !== 'folder' || String(item.id) !== String(destination.id)) &&
        canDragMoveItem(item) &&
        isMovableFolder(destination)
      : canMoveItemToRoot(item);

  const moveItemByDrag = async (
    item: FOLDER_CONTENT_ITEM,
    destination: (FOLDER_TYPE & { item_type?: 'folder' }) | null,
  ) => {
    if (
      !folderId ||
      !canDropOnNavigationTarget(item, destination)
    ) {
      return;
    }

    draggedMoveItemRef.current = null;
    setDraggedRowKey(null);
    setNavigationDropTargetKey(null);
    const previousFolderContents = folderContents;
    const previousTotal = total;
    const isSourceFolderOpen = () =>
      new URLSearchParams(history.location.search).get('folderId') === folderId;
    const moveAsReference =
      item.item_type !== 'folder' && Boolean((item as any).move_as_reference);
    setFolderContents((current) =>
      moveAsReference
        ? current.map((currentItem) =>
            currentItem.item_type === 'folder'
              ? currentItem
              : {
                  ...currentItem,
                  reference_timelines: (currentItem.reference_timelines || []).filter(
                    (timeline) => String(timeline.id) !== String(item.id),
                  ),
                },
          )
        : current.filter(
            (currentItem) =>
              !(
                currentItem.item_type === item.item_type &&
                String(currentItem.id) === String(item.id)
              ),
          ),
    );
    if (!moveAsReference) {
      setTotal((current) => Math.max(0, current - 1));
    }

    const showMoveUndo = beginMoveUndo();
    let undoMove: (() => Promise<unknown>) | undefined;
    try {
      if (item.item_type === 'folder') {
        undoMove = rememberFolderLocations([{ id: item.id, parent_folder_id: Number(folderId) }]);
        await patchMoveFolders(
          [item.id],
          destination?.id || null,
          encodeURI(destination
            ? `${PATHS.VIDEOFEEDS_LIST.path}?folderId=${destination.id}&parentPage=${PATHS.MY_FOLDERS.label}`
            : PATHS.MY_FOLDERS.path),
          encodeURI(destination
            ? `${PATHS.SHARED_VIDEOFEEDS_LIST.path}?folderId=${destination.id}&parentPage=${PATHS.SHARED_WITH_ME.label}`
            : PATHS.SHARED_WITH_ME.path),
        );
      } else if (destination) {
        const moveFeed = (fromId: string, toId: string) => patchMoveVideofeeds(fromId, toId, [
          {
            videofeedId: item.id,
            ...(moveAsReference ? { videofeedIds: [item.id] } : {}),
            urlPath: encodeURI(
              buildMovedVideofeedUrl(item, toId, 'my_folders'),
            ),
            sharedUrlPath: encodeURI(
              buildMovedVideofeedUrl(item, toId, 'shared_with_me'),
            ),
          },
        ]);
        await moveFeed(folderId, destination.id);
        undoMove = () => moveFeed(destination.id, folderId);
      }

      if (undoMove) showMoveUndo(`Moved to ${destination?.name || 'My Folders'}`, undoMove);
      dispatch(updateTriggerFolderFetch());
      if (isSourceFolderOpen()) {
        refreshFolderContents();
        fetchFolder(folderId);
      }
    } catch (err: any) {
      if (isSourceFolderOpen()) {
        setFolderContents(previousFolderContents);
        setTotal(previousTotal);
      } else {
        dispatch(updateTriggerFolderFetch());
      }
      notification.showMessage(
        err?.response?.data?.message || 'Failed to move item',
      );
    }
  };

  const getNavigationDropTitle = (destination: FOLDER_TYPE | null) => {
    const item = draggedMoveItemRef.current;
    if (!item) return '';
    if (!canDropOnNavigationTarget(item, destination)) {
      return !destination && item.item_type !== 'folder'
        ? 'Video files cannot be moved to the My Folders root'
        : 'You do not have permission to move this item here';
    }

    return `Move to ${destination?.name || 'My Folders'}`;
  };
  const getNavigationDropTargetClass = (
    targetKey: string,
    destination: FOLDER_TYPE | null,
  ) => {
    const isActive = navigationDropTargetKey === targetKey;
    const item = draggedMoveItemRef.current;
    return cx(
      styles.navigationDropTarget,
      isActive && item && canDropOnNavigationTarget(item, destination)
        ? styles.navigationDropTargetActive
        : '',
      isActive && item && !canDropOnNavigationTarget(item, destination)
        ? styles.navigationDropTargetInvalid
        : '',
    );
  };

  const onNavigationDragOver = (
    event: React.DragEvent<HTMLElement>,
    targetKey: string,
    destination: FOLDER_TYPE | null,
  ) => {
    const item = draggedMoveItemRef.current;
    if (!item) return;
    event.preventDefault();
    event.stopPropagation();
    event.dataTransfer.dropEffect = canDropOnNavigationTarget(item, destination)
      ? 'move'
      : 'none';
    setNavigationDropTargetKey(targetKey);
  };

  const onNavigationDrop = (
    event: React.DragEvent<HTMLElement>,
    destination: FOLDER_TYPE | null,
  ) => {
    event.preventDefault();
    event.stopPropagation();
    const item = draggedMoveItemRef.current;
    setNavigationDropTargetKey(null);
    if (!item) return;
    if (!canDropOnNavigationTarget(item, destination)) {
      notification.showMessage(getNavigationDropTitle(destination));
      return;
    }

    drag.reset();
    void moveItemByDrag(item, destination);
  };

  const onNavigationDragLeave = (
    event: React.DragEvent<HTMLElement>,
    targetKey: string,
  ) => {
    if (!event.currentTarget.contains(event.relatedTarget as Node)) {
      setNavigationDropTargetKey((current) =>
        current === targetKey ? null : current,
      );
    }
  };

  const canBulkMoveFeeds = selectedFeeds.length > 0 && selectedFolders.length === 0;
  const canBulkMoveMixed =
    selectedFeeds.length > 0 &&
    selectedFolders.length > 0 &&
    selectedFolders.every(canMoveFolderItem);
  const canBulkMoveFolders =
    selectedFolders.length > 0 &&
    selectedFeeds.length === 0 &&
    selectedFolders.every(canMoveFolderItem);
  const canBulkCopy =
    selectedFeedsForCopy.length > 0 &&
    selectedFolders.length === 0 &&
    selectedFeedsForCopy.every((feed) => feed.type === VIDEOFEED_TYPE.TIMELINE);
  const selectedActionCount = selectedFolders.length + selectedFeeds.length;

  const removeResumeUploadByFolderId = (targetFolderId: string) => {
    for (let index = localStorage.length - 1; index >= 0; index -= 1) {
      const key = localStorage.key(index);

      if (
        key &&
        key.startsWith('=videofeedToResume=') &&
        key.includes(`=${targetFolderId}::`)
      ) {
        localStorage.removeItem(key);
      }
    }
  };

  const removeResumeUploadByVideofeedId = (videofeedId: string) => {
    for (let index = localStorage.length - 1; index >= 0; index -= 1) {
      const key = localStorage.key(index);
      const value = key ? localStorage.getItem(key) : null;

      if (
        key &&
        key.startsWith('=videofeedToResume=') &&
        value &&
        value.includes(videofeedId)
      ) {
        localStorage.removeItem(key);
      }
    }
  };

  const onBulkMoveClick = () => {
    if (canBulkMoveMixed) {
      openMoveFoldersModal(selectedFolders, selectedFeeds);
      return;
    }

    if (!canBulkMoveFeeds) {
      return;
    }

    dispatch(updateFeedsToMove(selectedFeeds));
    dispatch(updateFeedTransferMode('move'));
    dispatch(updateShowMoveFeedModal(true));
  };

  const onBulkCopyClick = () => {
    if (!canBulkCopy) {
      return;
    }

    dispatch(
      updateFeedsToMove(
        selectedFeedsForCopy.map(
          (feed) =>
            ({
              ...(feed as any),
              copy_scope: (feed as any).move_as_reference
                ? 'timeline'
                : isCollectionDeleteTarget(feed)
                ? 'collection-video'
                : 'timeline',
            }) as VIDEOFEED,
        ),
      ),
    );
    dispatch(updateFeedTransferMode('copy'));
    dispatch(updateShowMoveFeedModal(true));
  };

  const onBulkDeleteConfirm = async () => {
    setBulkDeleteLoading(true);

    try {
      for (const folderToDelete of selectedFolders) {
        removeResumeUploadByFolderId(folderToDelete.id);
        await deleteFolderById(folderToDelete.id);
      }

      for (const feedToDelete of selectedFeeds) {
        removeResumeUploadByVideofeedId(feedToDelete.id);
        await deleteVideofeed({
          videofeedId: feedToDelete.id,
          confirm: true,
          deleteCollection: isCollectionDeleteTarget(feedToDelete),
        });
      }

      const organizationResponse = await getUserOrganization();
      dispatch(saveMyOrganization(organizationResponse.data));
      dispatch(updateTriggerFolderFetch());
      setShowBulkDeleteModal(false);
      clearSelection();
      setFetchTrigger({ bulkDeletedAt: Date.now() });

      if (folderId) {
        await fetchFolder(folderId);
      }
    } catch (err: any) {
      notification.showMessage(
        err?.response?.data?.message || NOTIFICATION.NOT_DELETED,
      );
    } finally {
      setBulkDeleteLoading(false);
    }
  };

  const openBulkDeleteModal = async () => {
    setBulkDeleteImpactLoading(true);
    setBulkDeleteImpactedComposers([]);
    setShowBulkDeleteModal(true);

    try {
      const impactedNames = new Set<string>();

      for (const folderToDelete of selectedFolders) {
        const response = await getFolderDeleteImpact(folderToDelete.id);
        const names = Array.isArray(response?.data) ? response.data : [];
        names.forEach((name: string) => {
          const normalized = String(name || '').trim();
          if (normalized) impactedNames.add(normalized);
        });
      }

      for (const feedToDelete of selectedFeeds) {
        const response = await deleteVideofeed({
          videofeedId: feedToDelete.id,
          confirm: false,
          deleteCollection: isCollectionDeleteTarget(feedToDelete),
        });
        const names = Array.isArray(response?.data) ? response.data : [];
        names.forEach((name: string) => {
          const normalized = String(name || '').trim();
          if (normalized) impactedNames.add(normalized);
        });
      }

      setBulkDeleteImpactedComposers([...impactedNames]);
    } catch {
      setBulkDeleteImpactedComposers([]);
    } finally {
      setBulkDeleteImpactLoading(false);
    }
  };

  const collaboratorData = useMemo(() => {
    if (!folder) return { direct: [], groups: [] };
    return {
      direct: folder.collaborators || [],
      groups: folder.group_collaborators || [],
    };
  }, [folder]);

  const collaboratorCount = useMemo(() => {
    if (!folder) return 0;
    const uniqueCollaboratorIds = new Set<string>();

    (folder.collaborators || []).forEach((user) => {
      if (user?.id) uniqueCollaboratorIds.add(String(user.id));
    });

    (folder.group_collaborators || []).forEach((group) => {
      (group?.analysts || []).forEach((user) => {
        if (user?.id) uniqueCollaboratorIds.add(String(user.id));
      });
    });

    if (folder.owner?.id) {
      uniqueCollaboratorIds.delete(String(folder.owner.id));
    }

    return uniqueCollaboratorIds.size;
  }, [folder]);

  const hasCollaborators =
    collaboratorData.direct?.length || collaboratorData.groups?.length;
  const showOwnerColumn = collaboratorCount > 0;

  useEffect(() => {
    if (!folder || showOwnerColumn) return;

    if (selectedOwners[0]?.value !== 'ALL') {
      setSelectedOwners([{ label: 'All', value: 'ALL' }]);
      setPage(0);
    }

    if (sortOption !== SortOptions.OWNER) return;
    const sortState = JSON.parse(getSortStateForMyFolders() || 'null');
    const fallbackSortType = sortState?.[SortOptions.UPDATED_AT] || SortType.ASC;
    dispatch(updateSortType(fallbackSortType));
    dispatch(updateSortOption(SortOptions.UPDATED_AT));
    setMyFoldersSortOptionAndType(fallbackSortType, SortOptions.UPDATED_AT);
    onPaginate(null, 0);
  }, [folder, showOwnerColumn, sortOption, selectedOwners, dispatch]);

  const videofeedsList = useMemo(
    () =>
      folderContents.filter(
        (item) => item.item_type !== 'folder',
      ) as VIDEOFEED[],
    [folderContents],
  );

  const orderedFeedsForIndex = useMemo(() => {
    const ordered: VIDEOFEED[] = [];
    const seen = new Set<string>();

    const append = (feed?: VIDEOFEED) => {
      if (!feed) return;
      const id = String(feed.id);
      if (seen.has(id)) return;
      seen.add(id);
      ordered.push(feed);
    };

    videofeedsList.forEach((feed) => {
      append(feed);
      (feed.reference_timelines || []).forEach((timeline) => append(timeline));
    });

    return ordered;
  }, [videofeedsList]);

  const feedIndexById = useMemo(
    () =>
      orderedFeedsForIndex.reduce<Record<string, number>>((acc, feed, index) => {
        acc[String(feed.id)] = index;
        return acc;
      }, {}),
    [orderedFeedsForIndex],
  );

  const nextFolderTrail = useMemo(() => {
    const trailParts = folderTrail ? folderTrail.split(',').filter(Boolean) : [];
    if (folderId && trailParts[trailParts.length - 1] !== folderId) {
      trailParts.push(folderId);
    }

    return trailParts.join(',');
  }, [folderId, folderTrail]);

  useEffect(() => {
    const visibleKeys = new Set<string>();
    folderContents.forEach((item) => {
      visibleKeys.add(getItemSelectionKey(item));
      if (item.item_type !== 'folder') {
        getSelectableReferenceTimelines(item).forEach((timeline) => {
          visibleKeys.add(getReferenceSelectionKey(timeline));
        });
      }
    });
    setSelectedItemKeys((prev) => prev.filter((key) => visibleKeys.has(key)));
  }, [folderContents]);

  useEffect(() => {
    clearSelection();
  }, [folderId]);

  useEffect(() => {
    if (selectMode) {
      clearSelection();
    }
  }, [refetchFeedList]);

  const contentEntries = folderContents.map((item, index) => ({ item, index }));
  const pinnedContentEntries = page === 0
    ? contentEntries.filter(({ item }) => Boolean((item as any)?.is_pinned))
    : [];
  const regularContentEntries = page === 0
    ? contentEntries.filter(({ item }) => !Boolean((item as any)?.is_pinned))
    : contentEntries;

  const drag = useContentDragDrop<FOLDER_CONTENT_ITEM>({
    items: folderContents,
    contextType: 'videofeeds',
    contextParentId: folderId,
    firstPage: page === 0,
    targetType: getPinTargetType,
    refresh: () => { setPage(0); refreshFolderContents(); },
    disabled: selectMode || loading || Boolean(groupFiles),
    canPin: (item) => !(item as VIDEOFEED & { move_as_reference?: boolean }).move_as_reference,
    canMove: (source, target) => target.item_type === 'folder' && canDropOnNavigationTarget(source, target),
    move: (source, target) => target.item_type === 'folder' ? moveItemByDrag(source, target) : Promise.resolve(),
    canGroup: (source, target) => Boolean(folderId) && canMoveFoldersByRole && source.item_type !== 'folder' && target.item_type !== 'folder'
      && !(source as any).move_as_reference && canDragMoveItem(source) && canDragMoveItem(target),
    group: (source, target) => setGroupFiles([source as VIDEOFEED, target as VIDEOFEED]),
    resolveItem: (item, event) => {
      if (item.item_type === 'folder') return item;
      const element = (event.target as HTMLElement).closest<HTMLElement>('[data-videofeed-id]');
      const reference = (item.reference_timelines || []).find((timeline) => String(timeline.id) === element?.dataset.videofeedId);
      if (!reference) return item;
      event.dataTransfer.setDragImage(element!, 20, 20);
      return { ...reference, move_as_reference: true } as VIDEOFEED;
    },
    onStart: (item) => {
      draggedMoveItemRef.current = canDragMoveItem(item) ? item : null;
      setDraggedRowKey(getItemSelectionKey(item));
    },
    onEnd: () => {
      draggedMoveItemRef.current = null;
      setDraggedRowKey(null);
      setNavigationDropTargetKey(null);
    },
  });

  const renderContentRow = ({ item, index }: { item: FOLDER_CONTENT_ITEM; index: number }) => (
    <div
      key={`${item.item_type || 'videofeed'}-${item.id}`}
      className={cx(
        styles.listRow,
        draggedRowKey === getItemSelectionKey(item) && styles.dragging,
      )}
      {...drag.rowProps(item)}
    >
      {drag.indicator(item)}
      {selectMode && (
        <div className={styles.selectionCheckbox}>
          <Checkbox
            checked={selectedItemKeySet.has(getItemSelectionKey(item))}
            onChange={() => toggleItemSelection(item)}
            sx={{
              color: 'rgba(255,255,255,0.6)',
              '&.Mui-checked': {
                color: '#39b3ea',
              },
            }}
          />
        </div>
      )}

      <div
        className={styles.listRowContent}
        style={{
          pointerEvents:
            selectMode && item.item_type === 'folder'
              ? 'none'
              : undefined,
        }}
      >
        {item.item_type === 'folder' ? (
          <ComposerFolder
            folder={item}
            folderIndex={index}
            data={{ page, limit: rowsPerPage, search, sort, sortOption }}
            showOwnerColumn={showOwnerColumn}
            contentLayout="videofeeds"
            fetchDataCallback={refreshFolderContents}
            canMove={canMoveFolderItem(item)}
            onMove={(targetFolder) =>
              openMoveFoldersModal([
                targetFolder as FOLDER_TYPE & { item_type: 'folder' },
              ])
            }
            onOpen={() => {
              if (String(item.id) === String(folderId)) {
                return;
              }

              setPage(0);
              history.push(
                `${PATHS.VIDEOFEEDS_LIST.path}?folderId=${item.id}&parentPage=${PATHS.MY_FOLDERS.label}${
                  nextFolderTrail
                    ? `&folderTrail=${encodeURIComponent(nextFolderTrail)}`
                    : ''
                }${
                  contentFilter !== FOLDER_CONTENT_FILTER.ALL
                    ? `&contentFilter=${contentFilter}`
                    : ''
                }`,
              );
            }}
            pinContextType="videofeeds"
            pinContextParentId={folderId}
            onPinUpdated={refreshFolderContents}
          />
        ) : (
          <Videofeed
            loading={loading}
            feedIndex={feedIndexById[String(item.id)] || 0}
            videofeed={item}
            fetchAll={fetchVideofeeds}
            allowAdditionalAngleUpload
            referenceTimelines={item.reference_timelines || []}
            feedIndexById={feedIndexById}
            forceMovieIcon={(item.reference_timelines?.length || 0) > 0}
            showOwnerColumn={showOwnerColumn}
            dateSortOption={sortOption}
            selectMode={selectMode}
            selectedReferenceKeys={selectedItemKeySet}
            onReferenceSelectionChange={(timeline) =>
              toggleReferenceSelection(timeline)
            }
            pinContextType="videofeeds"
            pinContextParentId={folderId}
            onPinUpdated={refreshFolderContents}
          />
        )}
      </div>
    </div>
  );

  return (
    <div className={styles.myFolders}>
      {groupFiles && folderId && (
        <CreateFolderFromDrop
          files={groupFiles}
          parentFolderId={folderId}
          onClose={() => setGroupFiles(null)}
          onUpdated={() => {
            refreshFolderContents();
            fetchFolder(folderId);
            dispatch(updateTriggerFolderFetch());
          }}
          move={(file, sourceId, destinationId) => patchMoveVideofeeds(sourceId, destinationId, [{
            videofeedId: file.id,
            urlPath: encodeURI(buildMovedVideofeedUrl(file, destinationId, 'my_folders')),
            sharedUrlPath: encodeURI(buildMovedVideofeedUrl(file, destinationId, 'shared_with_me')),
          }])}
        />
      )}
      <div className={styles.topBar}>
        <div className={styles.smallPath}>
          <span>File Management / </span>
          <Tooltip
            arrow
            open={navigationDropTargetKey === 'breadcrumb-root'}
            title={getNavigationDropTitle(null)}
          >
            <span
              className={getNavigationDropTargetClass('breadcrumb-root', null)}
              onDragOver={(event) => onNavigationDragOver(event, 'breadcrumb-root', null)}
              onDragLeave={(event) => onNavigationDragLeave(event, 'breadcrumb-root')}
              onDrop={(event) => onNavigationDrop(event, null)}
            >
              <button
                type="button"
                className={styles.breadcrumbButton}
                onClick={() => onBreadcrumbClick()}
              >
                My Folders
              </button>
            </span>
          </Tooltip>
          {breadcrumbFolders.map((breadcrumbFolder, index) => (
            <React.Fragment key={breadcrumbFolder.id}>
              <span className={styles.breadcrumbSeparator}> / </span>
              <Tooltip
                arrow
                open={navigationDropTargetKey === `breadcrumb-${breadcrumbFolder.id}`}
                title={getNavigationDropTitle(breadcrumbFolder)}
              >
                <span
                  className={getNavigationDropTargetClass(
                    `breadcrumb-${breadcrumbFolder.id}`,
                    breadcrumbFolder,
                  )}
                  onDragOver={(event) =>
                    onNavigationDragOver(event, `breadcrumb-${breadcrumbFolder.id}`, breadcrumbFolder)
                  }
                  onDragLeave={(event) =>
                    onNavigationDragLeave(event, `breadcrumb-${breadcrumbFolder.id}`)
                  }
                  onDrop={(event) => onNavigationDrop(event, breadcrumbFolder)}
                >
                  <button
                    type="button"
                    className={styles.breadcrumbButton}
                    onClick={() =>
                      onBreadcrumbClick(String(breadcrumbFolder.id), index)
                    }
                  >
                    {breadcrumbFolder.name}
                  </button>
                </span>
              </Tooltip>
            </React.Fragment>
          ))}
          {folder?.name && (
            <>
              <span className={styles.breadcrumbSeparator}> / </span>
              <span className={styles.breadcrumbCurrent}>{folder.name}</span>
            </>
          )}
        </div>
        <div className={styles.pageTitle}>
          {!!folderId && (
            <Tooltip
              arrow
              open={navigationDropTargetKey === 'back'}
              title={getNavigationDropTitle(breadcrumbFolders[breadcrumbFolders.length - 1] || null)}
            >
              <div
                className={getNavigationDropTargetClass(
                  'back',
                  breadcrumbFolders[breadcrumbFolders.length - 1] || null,
                )}
                onDragOver={(event) =>
                  onNavigationDragOver(
                    event,
                    'back',
                    breadcrumbFolders[breadcrumbFolders.length - 1] || null,
                  )
                }
                onDragLeave={(event) => onNavigationDragLeave(event, 'back')}
                onDrop={(event) =>
                  onNavigationDrop(
                    event,
                    breadcrumbFolders[breadcrumbFolders.length - 1] || null,
                  )
                }
              >
                <ContainedButton
                  kind="purple"
                  className={styles.backButton}
                  onClick={onHierarchyBackClick}
                >
                  <span className={styles.backButtonContent}>
                    <ArrowBack fontSize="small" />
                    Back
                  </span>
                </ContainedButton>
              </div>
            </Tooltip>
          )}
          <span>{folder?.name}</span>
          {folder && !!hasCollaborators && (
            <Tooltip
              arrow
              title={
                <div className={styles.collabTooltip}>
                  {hasCollaborators ? (
                    <>
                      <div className={styles.collabSectionTitle}>
                        Individual collaborators:
                      </div>
                      {collaboratorData.direct.map((u) => (
                        <div key={u.email} className={styles.collabRow}>
                          <UsersIcon className={styles.collabRowIcon} />
                          <span>{u?.username || (canViewEmails ? u.email : 'User')}</span>
                        </div>
                      ))}
                      <br />

                      {collaboratorData?.groups?.length > 0 && (
                        <div className={styles.collabSectionTitle}>
                          Group collaborators:
                        </div>
                      )}
                      {collaboratorData.groups.map((g) => (
                        <div key={g.title} className={styles.collabGroupBlock}>
                          {g.title}
                          <br />
                          {g.analysts.map((u) => (
                            <div key={u.email} className={styles.collabRow}>
                              <UsersIcon className={styles.collabRowIcon} />
                              <span>{u?.username || (canViewEmails ? u.email : 'User')}</span>
                            </div>
                          ))}
                        </div>
                      ))}
                    </>
                  ) : (
                    <div className={styles.collabEmpty}>
                      No other collaborators
                    </div>
                  )}
                </div>
              }
            >
              <div
                className={cx(
                  styles.collabIcon,
                  !hasCollaborators && styles.collabIconDisabled,
                )}
              >
                <UsersIcon />
              </div>
            </Tooltip>
          )}
        </div>
      </div>

      <div className={styles.searchWrapper}>
        <SearchInput quickFilter onChange={onSearch} className={styles.searchBar} />
        <ContainedButton
          className={styles.filterButton}
          onClick={switchFiltersModal}
          disabled={loading}
        >
          <FilterIcon />
          <span>Sort &amp; Filter</span>
          {(status !== VIDEOFEED_STATUS_FILTER.ALL ||
            contentFilter !== FOLDER_CONTENT_FILTER.ALL ||
            selectedOwners[0]?.value !== 'ALL') && (
            <span className={styles.filterBadge}>
              {Number(status !== VIDEOFEED_STATUS_FILTER.ALL) +
                Number(contentFilter !== FOLDER_CONTENT_FILTER.ALL) +
                Number(selectedOwners[0]?.value !== 'ALL')}
            </span>
          )}
        </ContainedButton>

        <div className={styles.selectionToolbar}>
          {selectMode && (
            <>
              <span className={styles.selectionCount}>
                {selectedItemKeys.length} selected
              </span>
              <Tooltip title="Only folders you own can be moved. Parent-organisation folders are locked.">
                <InfoOutlinedIcon className={styles.selectionInfoIcon} />
              </Tooltip>
              {(canBulkMoveFeeds || canBulkMoveMixed || canBulkMoveFolders) && (
                <button
                  type="button"
                  className={styles.selectionActionButton}
                  onClick={
                    canBulkMoveFolders
                      ? () => openMoveFoldersModal(selectedFolders)
                      : onBulkMoveClick
                  }
                >
                  <MoveButton />
                  <span>Move</span>
                </button>
              )}
              {canBulkCopy && (
                <button
                  type="button"
                  className={styles.selectionActionButton}
                  onClick={onBulkCopyClick}
                >
                  <ContentCopyIcon fontSize="small" />
                  <span>Copy</span>
                </button>
              )}
              <button
                type="button"
                className={styles.selectionIconButton}
                onClick={openBulkDeleteModal}
                disabled={!selectedItemKeys.length}
              >
                <TrashIcon />
              </button>
            </>
          )}

          {selectMode ? (
            <OutlinedButton
              kind="default"
              className={cx(
                styles.selectionToggleButton,
                styles.selectionToggleButtonActive,
              )}
              onClick={toggleSelectMode}
            >
              Cancel
            </OutlinedButton>
          ) : (
            <ContainedButton
              kind="purple"
              className={styles.selectionToggleButton}
              onClick={toggleSelectMode}
            >
              <span className={styles.selectionToggleContent}>
                <AddIcon className={styles.selectionToggleIcon} />
                <span>Select</span>
              </span>
            </ContainedButton>
          )}

          {!selectMode && (
            <>
              <Tooltip title="Deleted folders">
                <div className={styles.trashIcon} onClick={onDeletedFoldersClick}>
                  <FolderOffIcon />
                </div>
              </Tooltip>
              <Tooltip title="Deleted videofeeds">
                <div className={styles.trashIcon} onClick={onTrashIconClick}>
                  <TrashIcon />
                </div>
              </Tooltip>
            </>
          )}
        </div>
      </div>

      <div className={cx(styles.toolbar, selectMode && styles.toolbarSelectMode)}>
        <div className={styles.container}>
          <div className={styles.leftSide}>
            <div className={styles.name}>
              <button
                type="button"
                onClick={() => (loading ? '' : sortHandler(SortOptions.NAME))}
                className={styles.btn}
              >
                Name
                {sortOption === SortOptions.NAME &&
                  sort === SortType.ASC && (
                    <KeyboardArrowDownIcon className={styles.arrow} />
                  )}
                {sortOption === SortOptions.NAME &&
                  sort === SortType.DESC && (
                    <KeyboardArrowUpIcon className={styles.arrow} />
                  )}
              </button>
            </div>
          </div>
          <div
            className={cx(
              styles.rightSide,
              showOwnerColumn && styles.rightSideWithOwner,
            )}
          >
            {showOwnerColumn && (
              <div
                className={styles.owner}
                onClick={() =>
                  loading ? '' : sortHandler(SortOptions.OWNER)
                }
              >
                Owner
                {sortOption === SortOptions.OWNER &&
                  sort === SortType.DESC && (
                    <KeyboardArrowDownIcon className={styles.arrow} />
                  )}
                {sortOption === SortOptions.OWNER &&
                  sort === SortType.ASC && (
                    <KeyboardArrowUpIcon className={styles.arrow} />
                  )}
              </div>
            )}
            <div className={styles.share}>Share</div>
            <div className={styles.team}>
              {''}
            </div>
            <div className={styles.date}>
              <DateSortHeader
                sortOption={sortOption}
                sort={sort}
                compact={widthD <= 1330}
                disabled={loading}
                onSelect={sortHandler}
                arrowClassName={styles.arrow}
              />
            </div>
            <div
              className={styles.size}
              onClick={() => sortHandler(SortOptions.SIZE)}
            >
              {widthD > 1330 ? 'File Size' : 'Size'}
              {sortOption === SortOptions.SIZE && sort === SortType.DESC && (
                <KeyboardArrowDownIcon style={{ fontSize: '15px' }} />
              )}
              {sortOption === SortOptions.SIZE && sort === SortType.ASC && (
                <KeyboardArrowUpIcon style={{ fontSize: '15px' }} />
              )}
            </div>
            <div className={styles.duration}>
              {widthD > 1330 ? 'Upload Hours' : 'Hours'}
            </div>
            <div className={styles.actionBtns}>Actions</div>
          </div>
        </div>
      </div>

      <div className={styles.listScrollContainer}>
        {draggedRowKey && canScrollUp && (
          <div
            className={cx(styles.autoScrollZone, styles.autoScrollZoneTop)}
            onDragEnter={(event) => onAutoScrollDragOver(event, 'up')}
            onDragOver={(event) => onAutoScrollDragOver(event, 'up')}
            onDragLeave={onAutoScrollDragLeave}
            onDrop={(event) => {
              event.preventDefault();
              stopListAutoScroll();
            }}
          >
            <KeyboardArrowUpIcon />
          </div>
        )}
        <div
          ref={listRef}
          className={cx(styles.list)}
          id={'videofeed_custom_scroller'}
          onScroll={updateListScrollControls}
        >
          {(loading || drag.busy) && <LinearProgress />}
          {(pinnedContentEntries.length > 0 || drag.active) && (
            <div key="pinned-content" {...drag.zoneProps(true)}>
              <PinnedContentSection count={pinnedContentEntries.length}>
                {pinnedContentEntries.map(renderContentRow)}
                {drag.emptyHint(true, pinnedContentEntries.length === 0)}
              </PinnedContentSection>
            </div>
          )}
          <div key="regular-content" {...drag.zoneProps(false)}>
            {regularContentEntries.map(renderContentRow)}
            {drag.emptyHint(false, regularContentEntries.length === 0)}
          </div>
        </div>
        {draggedRowKey && canScrollDown && (
          <div
            className={cx(styles.autoScrollZone, styles.autoScrollZoneBottom)}
            onDragEnter={(event) => onAutoScrollDragOver(event, 'down')}
            onDragOver={(event) => onAutoScrollDragOver(event, 'down')}
            onDragLeave={onAutoScrollDragLeave}
            onDrop={(event) => {
              event.preventDefault();
              stopListAutoScroll();
            }}
          >
            <KeyboardArrowDownIcon />
          </div>
        )}
      </div>

      <PaginationFooter
        hideVersion
        page={page}
        total={total}
        onPageChange={onPaginate}
        rowsPerPage={rowsPerPage}
        onRowsPerPageChange={onRowsChange}
        pinnedCount={folderContents.filter((item: any) => item?.is_pinned).length}
      />

      <VideofeedFiltersModal
        open={filtersModalOpen}
        loading={loading}
        sortOption={sortOption}
        sortOrder={sort}
        status={status}
        contentFilter={contentFilter}
        selectedOwners={selectedOwners}
        canSortByOwner={showOwnerColumn}
        onClose={switchFiltersModal}
        onSort={sortFromFilters}
        onStatusChange={filterByStatus}
        onContentFilterChange={onContentFilterSelect}
        onOwnerChange={filterByOwner}
      />
      <MoveVideoFeedModal />
      <MoveFoldersModal
        open={showMoveFoldersModal}
        folders={foldersToMove}
        feeds={feedsToMoveWithFolders}
        sourceFolderId={folderId}
        sourceAncestorIds={breadcrumbAncestorIds}
        onClose={closeMoveFoldersModal}
        onMoved={() => {
          dispatch(updateTriggerFolderFetch());
          setFetchTrigger({ folderMovedAt: Date.now() });
          clearSelection();
          if (folderId) {
            fetchFolder(folderId);
          }
        }}
      />
      <VideofeedShareModal onUpdate={setFetchTrigger} />
      <ShareFolderModal
        setFetchTrigger={(n) => setFetchTrigger({ at: n })}
      />

      <Modal
        open={showBulkDeleteModal}
        onClose={() => setShowBulkDeleteModal(false)}
        loading={bulkDeleteLoading}
      >
        <div className={deleteModalStyles.modalBody}>
          <div className={deleteModalStyles.modalTitle}>
            Are you sure you want to delete {selectedActionCount}{' '}
            {selectedFolders.length > 0 ? 'items' : 'files'}?
          </div>
          <div className={deleteModalStyles.modalSubtitle}>
            {selectedFolders.length > 0
              ? `${selectedFolders.length} subfolder${
                  selectedFolders.length > 1 ? 's' : ''
                }, `
              : ''}
            {selectedFeeds.length} videofeed{selectedFeeds.length === 1 ? '' : 's'}
          </div>
          {!!selectedFeeds.length && (
            <div className={deleteModalStyles.restoreDisclaimer}>
              Deleted videofeeds can be restored for 30 days from Deleted videofeeds.
            </div>
          )}
          {!!selectedFolders.length && (
            <div className={deleteModalStyles.restoreDisclaimer}>
              Deleted folders can be restored for 30 days from Deleted folders.
            </div>
          )}
          {!bulkDeleteImpactLoading && !!bulkDeleteImpactedComposers.length && (
            <>
              <div className={deleteModalStyles.modalSubtitleSecondary}>
                Deleting this content will also remove clips from these Online Composers. Composers that only depend on this source will be deleted:
              </div>
              <div className={deleteModalStyles.namesList}>
                {bulkDeleteImpactedComposers.map((name) => (
                  <div key={name} className={deleteModalStyles.nameItem}>
                    {name}
                  </div>
                ))}
              </div>
            </>
          )}
          <div className={deleteModalStyles.actionBtns}>
            <OutlinedButton
              kind="default"
              disabled={bulkDeleteLoading || bulkDeleteImpactLoading}
              onClick={onBulkDeleteConfirm}
            >
              Yes, I am sure
            </OutlinedButton>
            <ContainedButton
              kind="purple"
              disabled={bulkDeleteLoading || bulkDeleteImpactLoading}
              onClick={() => setShowBulkDeleteModal(false)}
            >
              No, cancel
            </ContainedButton>
          </div>
        </div>
      </Modal>

      <DeletedVideofeedsModal
        open={showDeletedVideofeedsModal}
        switchModal={onTrashIconClick}
        onRestored={() => setFetchTrigger({ restoredAt: Date.now() })}
      />
      <DeletedFoldersModal
        open={showDeletedFoldersModal}
        switchModal={onDeletedFoldersClick}
        onRestored={() => setFetchTrigger({ restoredAt: Date.now() })}
      />
    </div>
  );
};

export default VideofeedsList;
